import { useMemo } from "react";
import { useAuth } from "../api/auth/useAuth";
import {
  getPlanTrialDays,
  isTrialActive,
  isTrialEligible,
} from "../api/services/pricing-plan.billing";
import { usePaymentInfo } from "../api/services/pricing-plan.hooks";
import type {
  PaymentInfoTrial,
  PricingPlan,
} from "../api/services/pricing-plan.type";
import { useShouldHideSubscriptions } from "./useShouldHideSubscriptions";

/**
 * Free-trial offer state for the signed-in user (docs/free-trial-design.md).
 *
 * The backend is the only source of truth: `payment_info.trial.eligible` already applies the
 * registration delay, the "never paid" rule and the one-trial-per-user rule. This hook only
 * adds the client-side guards (signed in, subscriptions not hidden) and plan helpers.
 */
export function useTrialOffer() {
  const { token } = useAuth();
  const hideSubscriptions = useShouldHideSubscriptions();
  const { data: paymentInfo, isLoading } = usePaymentInfo({
    enabled: Boolean(token),
  });

  const trial: PaymentInfoTrial | null = paymentInfo?.trial ?? null;
  const isEligible = Boolean(token) && !hideSubscriptions && isTrialEligible(paymentInfo);
  const isActive = isTrialActive(paymentInfo);

  return useMemo(
    () => ({
      isLoading,
      /** The user may start a trial on any plan that offers one. */
      isEligible,
      /** A trial is running (charged at `endsAt` unless cancelled). */
      isActive,
      status: trial?.status ?? "none",
      startedAt: trial?.started_at ?? null,
      endsAt: trial?.ends_at ?? null,
      chargeAmount: trial?.charge_amount ?? 0,
      chargeDate: trial?.charge_date ?? trial?.ends_at ?? null,
      /** Trial length for a given plan, 0 when that plan has no trial. */
      daysForPlan: (plan?: Pick<PricingPlan, "price"> | null) => getPlanTrialDays(plan),
      /** Whether this plan can be started as a trial by this user. */
      canStart: (plan?: Pick<PricingPlan, "price"> | null) =>
        isEligible && getPlanTrialDays(plan) > 0,
    }),
    [isLoading, isEligible, isActive, trial],
  );
}
